import { formatToCurrency } from "@shared/utils/formatters";
import {
  Card,
  ProductInfo,
  ProductNameContainer,
  Price,
  ProductDescription,
  TotalInfo,
  TotalItem,
  TrialLabel,
  PriceText,
  PriceTextLineThrough,
  PriceDiscountGroup,
  SubscriptionCancelledContainer,
  ActionsContainer,
  ActionButton,
} from "./styles";
import { PaidSubscriptionBreakdownProps } from "./SubscriptionBreakdown.types";
import { Button, Divider, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { format, formatDistanceToNow, isFuture } from "date-fns";
import { useUIStore } from "@store/ui.store";
import { blurActiveElement } from "@shared/utils/blur-active-element";
import { toast } from "@shared/services/toast";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

export const PaidSubscriptionBreakdown = (
  props: Omit<PaidSubscriptionBreakdownProps, "type">,
) => {
  const {
    price,
    productName,
    productDescription,
    trialEndDate,
    nextPaymentDate,
    lastPaymentDate,
    cancellationDate,
    scheduledCancellationDate,
    disabled,
    credits,
    estimatedPaymentMethodRequiredDate,
    userInfo,
    onRestoreSubscriptionClick,
    onUpdateTrialPeriodClick,
    handleCancelSubscription,
    handleCancelSubscriptionAtPeriodEnd,
    handleCancelScheduledSubscriptionCancellation,
    handleTurnToFree,
  } = props;
  const confirmDialog = useUIStore((state) => state.confirmDialog);
  const priceInDollars = price / 100;
  const creditsInDollars = credits / 100;
  const amountDueInDollars = priceInDollars - creditsInDollars;
  // TODO: Improve these conditions
  const hasEstimatedPaymentMethodRequiredDate =
    estimatedPaymentMethodRequiredDate &&
    isFuture(estimatedPaymentMethodRequiredDate) &&
    (!trialEndDate || !isFuture(trialEndDate));
  const isScheduledCancellationPending =
    !cancellationDate || (cancellationDate && !!scheduledCancellationDate);
  const isTrialing =
    isScheduledCancellationPending &&
    !hasEstimatedPaymentMethodRequiredDate &&
    !!trialEndDate &&
    isFuture(trialEndDate);
  const hasFreeTrialEndedAndNotPaidYet =
    !hasEstimatedPaymentMethodRequiredDate &&
    !!trialEndDate &&
    !isFuture(trialEndDate) &&
    !lastPaymentDate;

  const handleCancelSubscriptionClick = async () => {
    if (!handleCancelSubscription) return;
    const answer = await confirmDialog({
      title: "Cancel Subscription Now",
      content: !userInfo ? (
        <>
          <Typography>
            You are about to cancel your <strong>{productName}</strong>.
          </Typography>
          <Typography mt={2} mb={1} color="error">
            This action cannot be undone.
          </Typography>
        </>
      ) : (
        <>
          <Typography>
            You are about to cancel this <strong>{productName}</strong> for the
            following user:
          </Typography>
          <Typography mt={2}>
            <strong>Name:</strong> {userInfo.name}
          </Typography>
          <Typography mt={1}>
            <strong>E-mail:</strong> {userInfo.email}
          </Typography>
          <Typography mt={1}>
            <strong>Plan Name:</strong>{" "}
            {userInfo.subscriptionPlan?.name ?? "Unknown"}
          </Typography>
          <Typography mt={2} mb={1} color="error">
            This action will immediately cancel the subscription.
          </Typography>
        </>
      ),
      confirmButtonText: "Cancel Subscription",
      cancelButtonText: userInfo ? "Keep subscription" : "Keep my subscription",
      maxWidth: "sm",
    });

    blurActiveElement();

    if (!answer) return;

    toast.promise(
      handleCancelSubscription(),
      {
        pending: "Cancelling subscription...",
        success: "User subscription has been cancelled successfully.",
        error: "Failed to cancel user subscription.",
      },
      { autoClose: 10000 },
    );
  };

  const handleCancelSubscriptionAtPeriodEndClick = async () => {
    if (!handleCancelSubscriptionAtPeriodEnd) return;
    const answer = await confirmDialog({
      title: "Cancel Subscription at Renewal Date",
      content: !userInfo ? (
        <>
          <Typography>
            You are about to cancel your <strong>{productName}</strong>.
          </Typography>
          <Typography mt={2} mb={1} color="error">
            This action cannot be undone.
          </Typography>
        </>
      ) : (
        <>
          <Typography>
            After cancelling, the user will still have access to the
            subscription until the end of the current billing period.
          </Typography>
          <Typography mt={2}>
            <strong>Name:</strong> {userInfo.name}
          </Typography>
          <Typography mt={1}>
            <strong>E-mail:</strong> {userInfo.email}
          </Typography>
          <Typography mt={1}>
            <strong>Plan Name:</strong>{" "}
            {userInfo.subscriptionPlan?.name ?? "Unknown"}
          </Typography>

          {!!nextPaymentDate && (
            <Typography mt={1}>
              <strong>Next Billing Period:</strong>{" "}
              {format(nextPaymentDate, "MMM d, yyyy")}
            </Typography>
          )}
        </>
      ),
      confirmButtonText: "Cancel Subscription",
      cancelButtonText: userInfo ? "Keep subscription" : "Keep my subscription",
      maxWidth: "sm",
    });

    blurActiveElement();

    if (!answer) return;

    toast.promise(
      handleCancelSubscriptionAtPeriodEnd(),
      {
        pending: "Cancelling subscription...",
        success:
          "User subscription will be cancelled at the end of the current billing period.",
        error: "Failed to schedule user subscription cancellation.",
      },
      {
        autoClose: 10000,
      },
    );
  };

  const handleCancelScheduledSubscriptionCancellationClick = async () => {
    if (!handleCancelScheduledSubscriptionCancellation) return;
    const answer = await confirmDialog({
      title: "Renew Subscription",
      content: !userInfo ? (
        <>
          <Typography>
            You are about to renew your <strong>{productName}</strong>.
          </Typography>
        </>
      ) : (
        <>
          <Typography>
            This subscription will no longer be cancelled.
          </Typography>
          {!!nextPaymentDate && (
            <Typography>
              It will renew on {format(nextPaymentDate, "MMM d, yyyy")}.
            </Typography>
          )}
          <Typography mt={2}>
            <strong>Name:</strong> {userInfo.name}
          </Typography>
          <Typography mt={1}>
            <strong>E-mail:</strong> {userInfo.email}
          </Typography>
          <Typography mt={1}>
            <strong>Plan Name:</strong>{" "}
            {userInfo.subscriptionPlan?.name ?? "Unknown"}
          </Typography>
          {!!nextPaymentDate && (
            <Typography mt={1} mb={2}>
              <strong>Next Billing Period:</strong>{" "}
              {format(nextPaymentDate, "MMM d, yyyy")}
            </Typography>
          )}
        </>
      ),
      confirmButtonText: "Renew Subscription",
      cancelButtonText: "Go Back",
      maxWidth: "sm",
    });

    blurActiveElement();

    if (!answer) return;

    toast.promise(
      handleCancelScheduledSubscriptionCancellation(),
      {
        pending: "Renewing Subscription...",
        success:
          "User subscription will no longer be cancelled at the end of the current billing period.",
        error: "Failed to schedule user subscription cancellation.",
      },
      { autoClose: 10000 },
    );
  };

  const handleTurnToFreeClick = async () => {
    if (!handleTurnToFree) return;
    const answer = await confirmDialog({
      title: "Switch to Free Account",
      content: (
        <>
          <Typography>
            You are about to switch the following user&apos;s account to a{" "}
            <strong>free subscription</strong>:
          </Typography>
          <Typography mt={2}>
            <strong>Name:</strong> {userInfo?.name}
          </Typography>
          <Typography mt={1}>
            <strong>E-mail:</strong> {userInfo?.email}
          </Typography>
          <Typography mt={1}>
            <strong>Plan Name:</strong>{" "}
            {userInfo?.subscriptionPlan?.name ?? "Unknown"}
          </Typography>
          <Typography mt={2}>
            <strong>Note:</strong> The Stripe subscription state, including
            invoices and billing history, will remain unchanged. However,
            Sidekick will no longer use the Stripe subscription status to
            validate user access.
          </Typography>
        </>
      ),
      confirmButtonText: "Switch to Free",
      cancelButtonText: "Keep Paid Account",
      maxWidth: "sm",
    });

    blurActiveElement();

    if (!answer) return;

    toast.promise(
      handleTurnToFree(),
      {
        pending: "Switching subscription to free...",
        success: "User subscription has been switched to free successfully.",
        error: "Failed to switch user subscription to free.",
      },
      { autoClose: 10000 },
    );
  };

  const isCancellationDateAfterScheduledCancellationDate =
    cancellationDate &&
    scheduledCancellationDate &&
    cancellationDate > scheduledCancellationDate;

  const hasCancelledSubscription =
    isCancellationDateAfterScheduledCancellationDate ||
    (!scheduledCancellationDate && cancellationDate);

  const isCancellationScheduled =
    !isCancellationDateAfterScheduledCancellationDate &&
    scheduledCancellationDate;

  const isActionsEnabled =
    !!handleCancelSubscription ||
    !!handleCancelSubscriptionAtPeriodEnd ||
    !!handleCancelScheduledSubscriptionCancellation ||
    !!onUpdateTrialPeriodClick ||
    !!handleTurnToFree;

  return (
    <Card aria-disabled={disabled}>
      <ProductInfo>
        <ProductNameContainer>
          <Typography>{productName}</Typography>
          {isTrialing && (
            <TrialLabel $color="warning">
              Trial ends on {format(trialEndDate, "MMM d, yyyy")}
            </TrialLabel>
          )}
          {hasEstimatedPaymentMethodRequiredDate && (
            <Tooltip
              title={
                "The next charge is estimated based on your current credits and subscription details."
              }
            >
              <TrialLabel $color="warning">
                Next charge{" "}
                {formatDistanceToNow(estimatedPaymentMethodRequiredDate, {
                  addSuffix: true,
                })}
              </TrialLabel>
            </Tooltip>
          )}

          {hasFreeTrialEndedAndNotPaidYet && (
            <TrialLabel $color="error">
              Trial ended on {format(trialEndDate, "dd/MM/yyy")}
            </TrialLabel>
          )}
        </ProductNameContainer>
        {!!productDescription && (
          <ProductDescription>{productDescription}</ProductDescription>
        )}
      </ProductInfo>
      <TotalInfo>
        <TotalItem>
          <Typography fontWeight={500}>
            Billing monthly{" "}
            {credits > 0 && (
              <Tooltip
                title={
                  "You have subscription credits available. These will automatically be applied to your next payment, reducing the amount charged."
                }
              >
                <InfoOutlinedIcon fontSize="small" color="secondary" />
              </Tooltip>
            )}
          </Typography>
          <Price>
            {!!nextPaymentDate && (
              <span className="next-payment-date">
                {format(nextPaymentDate, "MMM d")} for
              </span>
            )}
            {credits <= 0 && (
              <PriceText data-testid="offer-price-after-discount">
                {formatToCurrency(priceInDollars)}
              </PriceText>
            )}
            {credits > 0 && (
              <PriceDiscountGroup>
                <PriceTextLineThrough data-testid="offer-price-after-discount">
                  {formatToCurrency(priceInDollars)}
                </PriceTextLineThrough>
                <PriceText data-testid="offer-price-after-discount">
                  {formatToCurrency(
                    amountDueInDollars < 0 ? 0 : amountDueInDollars,
                  )}
                </PriceText>
              </PriceDiscountGroup>
            )}
          </Price>
        </TotalItem>
      </TotalInfo>

      <Divider sx={{ margin: "0.5rem 1rem" }} />

      <ProductInfo>
        {(!!cancellationDate || !!scheduledCancellationDate) && (
          <SubscriptionCancelledContainer>
            {isCancellationScheduled && (
              <Typography variant="body2">
                <CalendarMonthIcon fontSize="small" /> Subscription will be
                paused on {format(scheduledCancellationDate, "MMM d, yyyy")}.
              </Typography>
            )}
            {hasCancelledSubscription && (
              <Typography variant="body2">
                Subscription paused on {format(cancellationDate, "MMM d, yyyy")}
                .
              </Typography>
            )}
            {onRestoreSubscriptionClick && (
              <Button onClick={onRestoreSubscriptionClick} size="small">
                Renew
              </Button>
            )}
            {handleCancelScheduledSubscriptionCancellation && (
              <Button
                onClick={handleCancelScheduledSubscriptionCancellationClick}
                size="small"
              >
                Renew
              </Button>
            )}
          </SubscriptionCancelledContainer>
        )}

        {isActionsEnabled && (
          <ActionsContainer>
            {!!handleCancelSubscriptionAtPeriodEnd && (
              <ActionButton
                type="button"
                size="small"
                onClick={handleCancelSubscriptionAtPeriodEndClick}
                disabled={disabled}
                variant="text"
                color="secondary"
              >
                {userInfo ? "Cancel Subscription at Renewal Date" : "Pause"}
              </ActionButton>
            )}

            {!!handleCancelSubscription && (
              <ActionButton
                type="button"
                size="small"
                onClick={handleCancelSubscriptionClick}
                disabled={disabled}
                variant="text"
                color="secondary"
              >
                {userInfo
                  ? "Cancel Subscription Now"
                  : "Manage My Subscription"}
              </ActionButton>
            )}

            {!!onUpdateTrialPeriodClick && (
              <ActionButton
                type="button"
                size="small"
                onClick={onUpdateTrialPeriodClick}
                disabled={disabled}
                variant="text"
                color="secondary"
              >
                Update Trial Period
              </ActionButton>
            )}

            {!!handleTurnToFree && (
              <ActionButton
                type="button"
                size="small"
                onClick={handleTurnToFreeClick}
                disabled={disabled}
                variant="text"
                color="secondary"
              >
                Switch to Free Account
              </ActionButton>
            )}
          </ActionsContainer>
        )}
      </ProductInfo>
    </Card>
  );
};
